import React, { useState } from "react";
import { signOut } from "../lib/auth";
import { openBillingPortal, startCheckout } from "../lib/subscription";
import { getTier, isSupremeActive, isSupremeEligible, SUPREME_ADDON, TIERS } from "../lib/subscriptionTiers";
import { exportCollectionCsv, exportCollectionPdf } from "../lib/export";
import { T } from "../theme";
import { Btn, Card, fmt, SectionTitle } from "../ui";

export default function ProfileScreen({ profile, tapes, wishlist, onOpenPrivacy, onOpenHelp }) {
  const [busyTier, setBusyTier] = useState(null);
  const [error, setError] = useState("");
  const totalValue = tapes.reduce((s, t) => s + Number(t.estimatedValue || 0), 0);
  const collectorSince = profile?.collectorSince?.toDate ? profile.collectorSince.toDate() : null;
  const currentTier = getTier(profile?.subscriptionTier);
  const isPaid = currentTier.key !== "stereo8";
  const supremeEligible = isSupremeEligible(profile);
  const supremeActive = isSupremeActive(profile);

  const upgrade = async (tierKey) => {
    setError("");
    setBusyTier(tierKey);
    try {
      const tier = TIERS[tierKey];
      await startCheckout(tier.priceId, tierKey);
    } catch (e) {
      setError(e.message || "Could not start checkout.");
      setBusyTier(null);
    }
  };

  const upgradeSupreme = async () => {
    setError("");
    setBusyTier("supreme");
    try {
      await startCheckout(SUPREME_ADDON.priceId, "supreme");
    } catch (e) {
      setError(e.message || "Could not start checkout.");
      setBusyTier(null);
    }
  };

  const manage = async () => {
    setError("");
    setBusyTier("manage");
    try {
      await openBillingPortal();
    } catch (e) {
      setError(e.message || "Could not open billing portal.");
      setBusyTier(null);
    }
  };

  return (
    <div>
      <Card style={{ textAlign: "center", background: `linear-gradient(135deg,${T.surface},${T.card})`, padding: "24px 16px" }}>
        <div style={{ width: 72, height: 72, borderRadius: 36, background: T.statBg, border: `3px solid ${T.accentAlt}`, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, overflow: "hidden" }}>
          {profile?.photoUrl ? <img src={profile.photoUrl} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "🎧"}
        </div>
        <div style={{ fontSize: 20, fontWeight: "bold", color: T.text, marginTop: 10 }}>{profile?.displayName || "Collector"}</div>
        {collectorSince && (
          <div style={{ fontSize: 11, color: T.muted, letterSpacing: "0.18em", marginTop: 4 }}>
            COLLECTOR SINCE {collectorSince.getFullYear()}
          </div>
        )}
        {isPaid && (
          <div style={{ display: "inline-block", marginTop: 8, background: T.accentAlt + "22", border: `1px solid ${T.accentAlt}55`, color: T.value, padding: "3px 14px", borderRadius: 20, fontSize: 11, letterSpacing: "0.1em" }}>
            ✓ Verified Collector
          </div>
        )}
      </Card>

      <SectionTitle>Membership</SectionTitle>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: "bold", color: T.text }}>{currentTier.name}</div>
            <div style={{ fontSize: 11, color: T.muted }}>{currentTier.tagline}</div>
          </div>
          {isPaid && <Btn variant="outline" disabled={busyTier === "manage"} onClick={manage} style={{ fontSize: 10, padding: "6px 12px" }}>{busyTier === "manage" ? "Opening…" : "Manage"}</Btn>}
        </div>
      </Card>

      {supremeEligible && (
        <Card style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: supremeActive ? `linear-gradient(135deg,${T.surface},${T.card})` : undefined }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: "bold", color: T.text }}>{SUPREME_ADDON.name} {supremeActive && "✓"}</div>
            <div style={{ fontSize: 11, color: T.muted }}>{SUPREME_ADDON.tagline}</div>
          </div>
          {!supremeActive && (
            <Btn variant="gold" disabled={busyTier === "supreme"} onClick={upgradeSupreme} style={{ fontSize: 11, padding: "8px 14px" }}>{busyTier === "supreme" ? "…" : "Add On"}</Btn>
          )}
        </Card>
      )}

      {error && <div style={{ color: "#C0392B", fontSize: 12, marginBottom: 10 }}>{error}</div>}

      {currentTier.key !== "vaultReserve" && (
        <>
          <SectionTitle>Upgrade</SectionTitle>
          {currentTier.key === "stereo8" && (
            <Card style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: "bold", color: T.text }}>{TIERS.quad8.name}</div>
                <div style={{ fontSize: 11, color: T.muted }}>250 tapes · 5 photos each · CSV export · wishlist alerts</div>
              </div>
              <Btn disabled={busyTier === "quad8"} onClick={() => upgrade("quad8")} style={{ fontSize: 11, padding: "8px 14px" }}>{busyTier === "quad8" ? "…" : "Upgrade"}</Btn>
            </Card>
          )}
          <Card style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: `linear-gradient(135deg,${T.surface},${T.statBg})` }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: "bold", color: T.text }}>{TIERS.vaultReserve.name}</div>
              <div style={{ fontSize: 11, color: T.muted }}>Unlimited everything · PDF export · featured listings</div>
            </div>
            <Btn variant="gold" disabled={busyTier === "vaultReserve"} onClick={() => upgrade("vaultReserve")} style={{ fontSize: 11, padding: "8px 14px" }}>{busyTier === "vaultReserve" ? "…" : "Upgrade"}</Btn>
          </Card>
        </>
      )}

      <SectionTitle>Collection Stats</SectionTitle>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
        {[[tapes.length, "Tapes"], [fmt(totalValue), "Est. Value"], [wishlist.length, "Wishlist"], [[...new Set(tapes.map((t) => t.genre).filter(Boolean))].length, "Genres"]].map(([v, l]) => (
          <div key={l} style={{ background: T.statBg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ fontSize: 9, color: T.muted, letterSpacing: "0.15em", textTransform: "uppercase" }}>{l}</div>
            <div style={{ fontSize: 18, fontWeight: "bold", color: T.accent, marginTop: 3 }}>{v}</div>
          </div>
        ))}
      </div>

      <SectionTitle>Export Collection</SectionTitle>
      <Card>
        {currentTier.exportFormats.length === 0 ? (
          <div style={{ fontSize: 12, color: T.muted }}>Upgrade to Quad-8 or Vault Reserve to export your collection.</div>
        ) : (
          <div style={{ display: "flex", gap: 10 }}>
            {currentTier.exportFormats.includes("csv") && (
              <Btn variant="outline" onClick={() => exportCollectionCsv(tapes)} style={{ flex: 1 }}>Export CSV</Btn>
            )}
            {currentTier.exportFormats.includes("pdf") && (
              <Btn variant="outline" onClick={() => exportCollectionPdf(tapes)} style={{ flex: 1 }}>Export PDF</Btn>
            )}
          </div>
        )}
      </Card>

      <SectionTitle>Settings</SectionTitle>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: `1px solid ${T.border}` }}>
        <span style={{ fontSize: 13, color: T.muted }}>Notification Preferences (coming soon)</span>
      </div>
      <div onClick={onOpenPrivacy} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: `1px solid ${T.border}`, cursor: "pointer" }}>
        <span style={{ fontSize: 13, color: T.text }}>Privacy &amp; Data</span>
        <span style={{ color: T.muted, fontSize: 14 }}>›</span>
      </div>
      <div onClick={onOpenHelp} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: `1px solid ${T.border}`, cursor: "pointer" }}>
        <span style={{ fontSize: 13, color: T.text }}>Help &amp; Support</span>
        <span style={{ color: T.muted, fontSize: 14 }}>›</span>
      </div>
      <div style={{ padding: "12px 0", borderBottom: `1px solid ${T.border}`, cursor: "pointer" }} onClick={() => signOut()}>
        <span style={{ fontSize: 13, color: "#C45A2D", fontWeight: "bold" }}>Log Out</span>
      </div>
      <div style={{ height: 20 }} />
    </div>
  );
}
